import { useI18n } from "@/shared/i18n"
import type { PlatformGift } from "@/shared/api/types"

import { MarketplaceCard } from "./MarketplaceCard"

interface MarketplaceGridProps {
  gifts: PlatformGift[]
  isLoading?: boolean
  isError?: boolean
  /** External id of a gift whose purchase is in flight (disables that card's button). */
  buyingGiftId?: string | null
  onBuy?: (gift: PlatformGift) => void
  onRetry?: () => void
}

const SKELETON_COUNT = 6

export function MarketplaceGrid({
  gifts,
  isLoading,
  isError,
  buyingGiftId,
  onBuy,
  onRetry,
}: MarketplaceGridProps) {
  const { t } = useI18n()

  // Empty cache + first fetch → skeleton placeholders.
  if (isLoading && gifts.length === 0) {
    return (
      <div className="grid grid-cols-3 gap-x-5 gap-y-6 p-4">
        {Array.from({ length: SKELETON_COUNT }).map((_, idx) => (
          <div key={idx} className="flex w-full flex-col items-center gap-3">
            <div className="flex w-full flex-col items-start gap-1">
              <div className="relative w-full pb-2.5">
                <div className="h-28 w-full animate-pulse rounded-2xl bg-secondary-main" />
                <div className="absolute bottom-0 left-1/2 h-5 w-12 -translate-x-1/2 animate-pulse rounded-full bg-white/50" />
              </div>
              <div className="h-4 w-full animate-pulse rounded bg-secondary-main" />
            </div>
            <div className="h-7 w-full animate-pulse rounded-full bg-[#00FE74]/30" />
          </div>
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
        <span className="text-sm text-white/60">
          {t("marketplace.loadFailed")}
        </span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-white"
          >
            {t("marketplace.retry")}
          </button>
        )}
      </div>
    )
  }

  if (gifts.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
        <span className="text-base font-medium text-white">
          {t("marketplace.emptyTitle")}
        </span>
        <span className="max-w-[280px] text-sm text-white/60">
          {t("marketplace.emptyHint")}
        </span>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-3 gap-x-5 gap-y-6 p-4">
      {gifts.map((gift) => (
        <MarketplaceCard
          key={gift.external_gift_id}
          gift={gift}
          isBuying={buyingGiftId === gift.external_gift_id}
          onBuy={() => onBuy?.(gift)}
        />
      ))}
    </div>
  )
}
